import { computed, ref } from "@vue/composition-api";
import { PageableSearchResult, SearchResult, SortableSearch } from "@scrile/api-provider/dist/api/types";

interface PageableState<T> extends PageableSearchResult, SearchResult<T>, SortableSearch {}

export default function usePager<T, D>(
  loader: (page: number, requestData?: D) => Promise<PageableState<T>>,
  onGetData?: (response: T[]) => void
) {
  const enableScroll = ref(false);
  const data = ref<PageableState<T>>();
  const isLastPage = computed(() => data.value && !data.value?.hasNextPage);

  const items = computed(() => {
    return data.value?.result ?? [];
  });

  const getData = async (page: number = 0, requestData?: D, replace?: boolean) => {
    try {
      enableScroll.value = false;
      const response = await loader(page, requestData);
      data.value = {
        ...response,
        result: !data.value || replace ? response.result : data.value.result.concat(response.result),
      };
      onGetData && onGetData(data.value?.result ?? []);
    } finally {
      enableScroll.value = data.value?.hasNextPage ?? false;
    }
  };

  const getNextPage = async (cb?: Function, requestData?: D) => {
    if (!enableScroll.value) {
      return;
    }
    const nextPage = (data.value?.page ?? 0) + 1;
    await getData(nextPage, requestData, false);
    cb && cb();
  };

  const getFirstPage = (requestData?: D) => {
    return getData(0, requestData, true);
  };

  const getLostItem = async (requestData?: D) => {
    if (!data.value || !data.value?.hasNextPage) return;
    try {
      enableScroll.value = false;
      const response = await loader(data.value.result.length, Object.assign({ size: 1 }, requestData));
      data.value = {
        ...response,
        page: data.value.page,
        result: !data.value ? response.result : data.value.result.concat(response.result),
      };
    } finally {
      enableScroll.value = (data.value?.hasNextPage || data.value?.hasPrevPage) ?? false;
    }
  };

  const removeItem = async (comparator: (item: T) => boolean) => {
    if (!data.value) return;
    data.value = {
      ...data.value,
      result: data.value.result.filter(i => !comparator(i)),
      totalRecords: data.value.totalRecords - 1,
    };
    await getLostItem();
  };

  const updateItem = (newItem: T, comparator: (i1: T, i2: T) => boolean) => {
    if (!data.value) return;
    data.value = {
      ...data.value,
      result: data.value.result.map(i => (comparator(i, newItem) ? newItem : i)),
    };
  };

  const addItem = (newItem: T, place: "start" | "end" = "end") => {
    if (!data.value) return;
    data.value = {
      ...data.value,
      result: place === "start" ? [newItem].concat(data.value.result) : data.value.result.concat([newItem]),
    };
    // todo: add support for prevPage
    if (data.value.hasNextPage) {
      place === "start" ? data.value.result.pop() : data.value.result.shift();
      data.value = {
        ...data.value,
        result: data.value.result.filter((i, index, array) =>
          place === "start" ? index !== array.length : index !== 0
        ),
      };
    }
  };

  return {
    enableScroll,
    data,
    items,
    isLastPage,
    getFirstPage,
    getNextPage,
    getLostItem,
    removeItem,
    updateItem,
    addItem,
  };
}
